import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Eye,
  Heart,
  Award,
  Users,
  Globe,
  CheckCircle,
  Building2,
} from "lucide-react";
import {
  CASE_STUDIES,
  COMPANY_INFO,
  INDUSTRIES,
  INDUSTRY_ICONS,
  STATS,
} from "../data/mock";
import {
  WhatWeDoSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
} from "../components/CommonSections";

const AboutCompanyPage = () => {
  const coreStrengths = [
    "Engineering intelligent, production-grade AI agents and applications​",
    "Rapid GenAI deployments from MVP to enterprise scale​",
    "AI-powered voice agents, workflow engines, and decision support systems​",
    "Customizable AI infrastructure built on microservice-based architecture​",
    "Deep NLP and LLM integration expertise​",
    "IoT and IIoT Solutions​",
    "Embedded Services, Machine Learning and Prototyping​",
    "Native and hybrid mobile apps development​",
    "Web applications and cloud-based development​",
    "Connected devices​",
    "Analytics engine​",
  ];

  const aiStrengths = [
    "Engineering intelligent, production-grade AI agents and applications​",
    "Rapid GenAI deployments from MVP to enterprise scale​",
    "AI-powered voice agents, workflow engines, and decision support systems​",
    "Customizable AI infrastructure with microservice-based Embedded​",
    "Deep NLP and LLM integration expertise​",
  ];

  const platformStrengths = [
    "IoT and IIoT Solutions​",
    "Embedded Services, Machine Learning and Prototyping​",
    "Native and hybrid mobile apps development​",
    "Web applications and cloud-based development​",
    "Connected devices​",
    "Real-time analytics and decision intelligence​",
  ];

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description:
        "We don't just ship — we engineer. Every product we build is designed for performance, reliability, and long-term scale, not just to meet a deadline.",
    },
    {
      icon: Heart,
      title: "Integrity",
      description:
        "We say what we mean and do what we say. No surprises, no hand-waving — just honest communication and clear accountability from day one.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description:
        "We embed ourselves in your team, not above it. Your goals are our goals, and we build solutions with you, not just for you.",
    },
    {
      icon: Award,
      title: "Innovation",
      description:
        "We don't chase trends — we adopt the right technologies at the right time. Our AI-first approach means we're always building for where the world is going.",
    },
  ];

  const milestones = [
    {
      year: "2017",
      points: [
        "Started operations with GDC for <b>Big Kitty Labs</b> Setup",
        "Setup development center from scratch",
      ],
    },
    {
      year: "2018",
      points: [
        "Setup DC for <b>Clix Capital</b> ",
        "Demonstrated ability to scale quickly",
      ],
    },

    {
      year: "2019",
      points: [
        "Continued work for <b>Big Kitty Labs</b> and <b>Clix Capital</b> Supporting <b>Big Kitty Labs</b> Client Base",
        ,
      ],
    },
    {
      year: "2020",
      points: [
        "Successfully continued operations amidst global disruption​",
        "Strengthened delivery model with agile delivery and remote-first practices​",
      ],
    },
    {
      year: "2021",
      points: [
        "Onboarded <b>Alaan</b> as a new client marking expansion into modern expense management space",
      ],
    },
    {
      year: "2022",
      points: ["Setup MDC (Mobile App Development Center) for <b>Bitwise</b>"],
    },
    {
      year: "2023",
      points: [
        "Embarking on the AI journey with focus on development Web Applications using AI",
      ],
    },
    {
      year: "2024",
      points: [
        "Focus on reinventing development Web Applications with use of AI",
        "Building AI-enabled features",
        "Partnership with <b>Newtuple</b> to leverage their AI platform",
        "Strategic focus on AI with multiple initiatives",
        "Onboarding AI enabled talent",
      ],
    },
    {
      year: "2025",
      points: [
        "Reskilling and upskilling workforce for AI-first delivery",
        "Forging partnerships with specialist AI platforms",
        "Launching AI consulting and advisory offerings",
        "Expanding our Offshore Delivery Centre",
        "Onboarding enterprise clients across regulated industries",
      ],
    },
    {
      year: "2026",
      points: [
        "Expanded presence in US and Europe",
        "Transitioned into an AI-first transformation partner",
        "Invested in elite engineering, QA, and AI talent development programs",
        "Positioning as a growth-focused, innovation-driven technology partner",
        "Focused on building scalable, adaptive, and future-ready digital systems",
      ],
    },
  ];

  const parseHtml = (text) => {
    return (
      <span
        dangerouslySetInnerHTML={{ __html: text }}
        className="[&_b]:font-semibold [&_b]:text-gray-900"
      />
    );
  };
  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b32126] font-semibold">About Us</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              About {COMPANY_INFO.name}
            </h1>
            <p className="text-xl text-gray-300">
              We are an AI-native engineering company helping businesses
              automate intelligently, scale confidently, and build software that
              lasts — from MVP to enterprise.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-gray-600 mb-4">
                Founded in 2017, <b>{COMPANY_INFO.name}</b> began as a product
                engineering company focused on web and mobile development. Over
                the years, we've evolved into an AI-native engineering partner —
                building production-grade AI agents, GenAI platforms, and
                enterprise automation systems. With deep investment in AI talent
                and strategic industry partnerships, we now power intelligent
                operations for clients across telecom, fintech, healthcare, and
                other regulated industries.
              </p>
              {/* <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Our Core Strengths
              </h2>
              <ul className="grid gap-2">
                {coreStrengths.map((perk, index) => (
                  <li key={index} className="flex items-start"> */}
              {/* Circle bullet */}
              {/* <span className="w-1.5 h-1.5 bg-black rounded-full mr-3 mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{perk}</span>
                  </li>
                ))}
              </ul> */}

              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                AI & Emerging Tech
              </h2>
              <ul className="grid gap-2 mb-8">
                {aiStrengths.map((perk, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-3 mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{perk}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Platform Engineering
              </h2>
              <ul className="grid gap-2">
                {platformStrengths.map((perk, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-3 mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{perk}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#b32126] text-white p-6 rounded-xl">
                <div className="text-4xl font-bold">9+</div>
                <div className="text-red-100">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#b32126] mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-[#b32126]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-gray-600">
                {/* To empower organizations with secure, scalable, and intelligent
                digital solutions that create real business impact. We combine
                AI innovation, modern mobile and web technologies, and agile
                execution to transform ideas into high-performing digital
                products. */}
                We empower organizations with secure, scalable, and intelligent
                digital solutions — combining AI innovation, modern mobile and
                web technologies, and agile execution to turn ideas into
                high-performing products that drive measurable business impact.
              </p>
              <br />
              <p className="text-gray-600">
                We focus on rapid delivery, strong architecture, and long-term
                scalability — ensuring every solution we build drives
                efficiency, growth, and measurable outcomes.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-[#b32126]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600">
                To become a globally trusted technology partner known for
                engineering future-ready AI, mobile, and web platforms that
                enable sustainable growth.
              </p>
              <br />
              <p className="font-semibold text-gray-900">
                We aim to help businesses innovate faster, operate smarter, and
                scale confidently in a rapidly evolving digital world—by
                building products that are not just technically strong, but
                strategically impactful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center"
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-[#b32126]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones in our growth story
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200 hidden md:block" />
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div
                    className={`flex-1 ${
                      index % 2 === 0 ? "md:text-right" : "md:text-left"
                    }`}
                  >
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 inline-block">
                      <div className="text-[#b32126] font-bold text-lg mb-1">
                        {milestone.year}
                      </div>
                      {/* <h4 className="font-semibold text-gray-900 mb-1">
                        {milestone.title}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {milestone.description}
                      </p> */}
                      <ul className={`space-y-2 text-gray-600 text-sm `}>
                        {milestone.points.map((point, pointIndex) => (
                          <li key={pointIndex} className="leading-relaxed">
                            {parseHtml(point)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-[#b32126] rounded-full flex-shrink-0 hidden md:block relative z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />

      {/* Industries Section - Show first 2 rows (12 items on desktop) */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Deep expertise across {INDUSTRIES.length}+ industries, delivering
              specialized solutions
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {INDUSTRIES.slice(0, 12).map((industry) => {
              const IconComponent = INDUSTRY_ICONS[industry.slug] || Building2;
              return (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="group p-4 bg-gray-800 rounded-xl text-center hover:bg-[#b32126] transition-all"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <IconComponent className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-gray-300 group-hover:text-white text-sm font-medium block">
                    {industry.title}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/industries"
              className="inline-flex items-center text-[#b32126] hover:text-[#d42a30] font-semibold"
            >
              View All Industries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />

      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve your technology goals.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Get in Touch
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutCompanyPage;
